import { useApprovalGroupForm } from "./approval-group-context";
import {
  Badge,
  Heading,
  TextAreaField,
  TextField,
} from "@elaniintc/goes-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import {
  newApprovalGroupSchema,
  type ApprovalGroupFormData,
} from "@/types/entities/approval-groups";
import { InputCounter } from "@/pages/portal/suspensions/components/InputCounter";

const SENIORITY = {
  SENIOR: 1,
  MEDIO: 2,
  JUNIOR: 3,
};
type SENIORITY = (typeof SENIORITY)[keyof typeof SENIORITY];
const initialValues: Record<SENIORITY, { amount: number }> = {
  [SENIORITY.SENIOR]: { amount: 0 },
  [SENIORITY.MEDIO]: { amount: 0 },
  [SENIORITY.JUNIOR]: { amount: 0 },
};

export const GroupForm = ({ readonly }: { readonly?: boolean }) => {
  const [seniorityValues, setSeniorityValues] =
    useState<Record<SENIORITY, { amount: number }>>(initialValues);

  const {
    formData,
    setIsFormValid,
    setIsSubmitting,
    selectedUsers,
    onSave,
    apiErrors,
  } = useApprovalGroupForm();

  const {
    register,
    watch,
    formState: { errors, isValid },
    handleSubmit,
    reset,
    setError,
  } = useForm({
    resolver: zodResolver(newApprovalGroupSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      ...formData,
      type: formData?.type || "approval",
    },
  });

  const [description] = watch(["description"]);

  useEffect(() => {
    const newValues = { ...initialValues };

    formData.seniorityValues?.forEach((item) => {
      const key = Number(Object.keys(item)[0]) as SENIORITY;
      // Solo cargar el valor del técnico (3), líder (1) y supervisor (2) siempre en 0
      if (key === SENIORITY.JUNIOR) {
        newValues[key] = { amount: item[key].amount };
      }
    });

    setSeniorityValues(newValues);
  }, [formData.seniorityValues]);

  // Reset form when formData changes (for editing existing groups)
  useEffect(() => {
    reset({
      ...formData,
      type: formData?.type || "approval",
    });
  }, [formData, reset]);

  // Sync API errors into form errors
  useEffect(() => {
    Object.keys(apiErrors).forEach((field) => {
      setError(field as keyof typeof errors, {
        type: "deps",
        message: apiErrors[field],
      });
    });
  }, [apiErrors, setError]);

  // Update form validity in context
  useEffect(() => {
    setIsFormValid(isValid);
  }, [isValid, setIsFormValid]);

  const onSubmit = async (data: ApprovalGroupFormData) => {
    setIsSubmitting(true);
    try {
      if (onSave) {
        await onSave(data, selectedUsers, seniorityValues);
      } else {
        // Fallback for when no onSave is provided (shouldn't happen in practice)
        console.log("Form data:", data);
        console.log("Selected users:", selectedUsers);
      }
    } catch (error: unknown) {
      console.error("Error saving group:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  function addSeniorityValue(amount: number, seniority: SENIORITY): void {
    setSeniorityValues((prev) => ({
      ...prev,
      [seniority]: { amount },
    }));
  }

  return (
    <div className="flex flex-col gap-8">
      <form
        id="new-group-form"
        className="flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Heading variant="h5">Datos del grupo</Heading>
        <TextField
          label="Nombre"
          placeholder="Nombre del grupo"
          required
          {...register("name")}
          error={errors.name?.message as string | undefined}
          readOnly={readonly}
        ></TextField>
        <TextAreaField
          label="Descripción"
          limit={200}
          count={description?.length || 0}
          placeholder="Describe este grupo..."
          rows={3}
          className="resize-none w-full bg-neutral-object-accent-silent border-neutral"
          {...register("description")}
          error={errors.description?.message as string | undefined}
          readOnly={readonly}
        />
        <input type="hidden" {...register("type")} value="approval" />
      </form>
      <div className="flex flex-col gap-4">
        <Heading variant="h5">Nivel de los miembros</Heading>
        <p className="text-size-body text-body">
          El nivel de un miembro corresponde al nivel de conocimiento que tiene
          sobre el trámite que gestionará. La lógica de asignación para nuevas
          solicitudes tomará en cuenta este nivel para saber cuanta carga puede
          llevar exitosamente. El nivel es asignado por cada grupo.
        </p>

        <div className="flex flex-col gap-2.5">
          {/* Líder */}
          <div className="flex flex-col justify-between gap-2.5 text-body text-size-body">
            <div className="flex gap-2.5 items-center">
              <Badge className="rounded-lg" variant="success">
                Líder
              </Badge>
              <span className="text-xs text-body-quiet">
                Sin límite de carga
              </span>
            </div>
            <span className="text-body">
              Miembro senior encargado del grupo (solo hay uno).
            </span>
          </div>
          {/* Supervisor */}
          <div className="flex flex-col justify-between gap-2.5 text-body text-size-body">
            <div className="flex gap-2.5 items-center">
              <Badge className="rounded-lg" variant="danger">
                Supervisor
              </Badge>
              <span className="text-xs text-body-quiet">
                Sin límite de carga
              </span>
            </div>
            <span className="text-body">
              Visualiza y reasigna tramites de los diferentes integrantes del
              grupo
            </span>
          </div>
          {/* Técnico aka Junior */}
          <div className="flex flex-col justify-between gap-2.5 text-body text-size-body">
            <div className="flex gap-2.5 items-center">
              <Badge className="rounded-lg" variant="action">
                Técnico
              </Badge>
              <InputCounter
                label=""
                value={seniorityValues[SENIORITY.JUNIOR].amount}
                onChange={(val) => addSeniorityValue(val, SENIORITY.JUNIOR)}
                min={0}
                max={90}
              />
              <span className="text-xs text-body-quiet">
                Configura el límite de carga
              </span>
            </div>
            <span className="text-body">
              Con mucha experiencia sobre el trámite y sus variantes.
            </span>
          </div>
          {/* Medio */}
          {/* <div className="flex flex-col justify-between gap-2.5 text-body text-size-body">
            <div className="flex gap-2.5 items-center">
              <Badge className="rounded-lg" variant="info">
                Medio
              </Badge>
              <InputCounter
                label=""
                value={seniorityValues[SENIORITY.MEDIO].amount}
                onChange={(val) => addSeniorityValue(val, SENIORITY.MEDIO)}
                min={0}
                max={90}
              />
              <span className="text-xs text-[#6D87A3]">
                Configura el límite de carga
              </span>
            </div>
            <span className="text-[#506981]">
              Con moderada experiencia y conocimiento del trámite.
            </span>
          </div> */}
          {/* Junior */}
          {/* <div className="flex flex-col justify-between gap-2.5 text-body text-size-body">
            <div className="flex gap-2.5 items-center">
              <Badge className="rounded-lg" variant="warning">
                Junior
              </Badge>
              <InputCounter
                label=""
                value={seniorityValues[SENIORITY.JUNIOR].amount}
                onChange={(val) => addSeniorityValue(val, SENIORITY.JUNIOR)}
                min={0}
                max={90}
              />
              <span className="text-xs text-[#6D87A3]">
                Configura el límite de carga
              </span>
            </div>
            <span className="text-[#506981]">
              Con poca experiencia y poco conocimiento del trámite
            </span>
          </div> */}
        </div>
      </div>
    </div>
  );
};
